import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FixedScrollWrapper from 'dpl/components/FixedScrollWrapper';
import OverlayModal from 'dpl/components/OverlayModal';
import MiniOverlayModalCloseButton from 'dpl/common/mini_overlay_modal/components/MiniOverlayModalCloseButton';
import { isNativeAppUA } from 'dpl/shared/utils';

const miniOverlayModalDefaultPropOnCloseClick = () => {};
/** @param {any} props */
export default function MiniOverlayModal({
  children,
  className = '',
  hideCloseButton = isNativeAppUA(),
  disableClose = false,
  maxWidth = '600px',
  onCloseClick = miniOverlayModalDefaultPropOnCloseClick,
  onOpen = null,
  scrollableContent = false,
  zIndexClass = 'z-max',
  contentClassName = 'bg-white',
  mobileCompact = false,
  closeButtonColor = 'secondary',
  closeButtonClassName = '',
  closeButtonSize = '20px',
  dataTestId = 'mini-overlay-modal',
  fullScreen = false,
  shouldCloseOnOverlayClick = true,
  fixedScrollWrapperClassName = ''
}) {
  const ref = useRef();

  const bgClassName = classnames('flex justify-center items-center', {
    'pv12-md ph8-md': !fullScreen,
    'bg-black-90': fullScreen,
    'h-100': scrollableContent,
    'min-h-100': !scrollableContent
  });

  const contentsClassName = classnames(
    'MiniOverlayModal__contents w-100 relative-ns center',
    {
      'h-100': scrollableContent,
      relative: mobileCompact,
      br4: !fullScreen
    },
    contentClassName
  );

  function onOverlayClick(e) {
    if (shouldCloseOnOverlayClick && e.target === ref.current) {
      onCloseClick();
    }
  }

  /* eslint-disable jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events */
  return (
    <OverlayModal
      className={className}
      hideCloseButton
      onClose={onCloseClick}
      onOpen={onOpen}
      zIndexClass={zIndexClass}
      disableClose={disableClose}
      dataTestId={dataTestId}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    >
      <FixedScrollWrapper
        className={classnames(
          'MiniOverlayModal overflow-x-hidden h-100',
          {
            'MiniOverlayModal--mobileCompact': mobileCompact
          },
          fixedScrollWrapperClassName
        )}
      >
        <div
          className={bgClassName}
          ref={ref}
          role="button"
          onClick={onOverlayClick}
        >
          <div className={contentsClassName} style={{ maxWidth }}>
            {children}
            {!hideCloseButton && (
              <MiniOverlayModalCloseButton
                onCloseClick={onCloseClick}
                iconSize={closeButtonSize}
                color={closeButtonColor}
                className={closeButtonClassName}
              />
            )}
          </div>
        </div>
      </FixedScrollWrapper>
    </OverlayModal>
  );
}

MiniOverlayModal.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  hideCloseButton: PropTypes.bool,
  closeButtonColor: PropTypes.string,
  closeButtonSize: PropTypes.string,
  disableClose: PropTypes.bool,
  maxWidth: PropTypes.string,
  onCloseClick: PropTypes.func,
  onOpen: PropTypes.func,
  scrollableContent: PropTypes.bool,
  zIndexClass: PropTypes.string,
  contentClassName: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  mobileCompact: PropTypes.bool, // allow "peeking" transparent background
  dataTestId: PropTypes.string,
  fullScreen: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
  fixedScrollWrapperClassName: PropTypes.string
};
